import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Heart, Zap, Award, Leaf, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/ui/motion";

const ICONS = { v1: Heart, v2: Zap, v3: Award, v4: Leaf };
const ease = [0.22, 1, 0.36, 1];

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <Seo titleKey="seo.about_title" descKey="seo.about_desc" path="/a-propos" />

      {/* ── 1. HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40" />
          <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-emerald-200/40 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-[20rem] w-[20rem] rounded-full bg-sky-200/30 blur-3xl" />
        </div>
        <div className="container-x pt-16 pb-20 lg:pt-24 lg:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
          >
            <div className="pill mb-5 w-fit">
              <span>{t("nav.about")}</span>
            </div>
            <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-[4rem] text-slate-900 leading-[1.02] max-w-3xl">
              {t("about.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {t("about.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 2. MISSION + IMAGE ────────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
          >
            <p className="text-base sm:text-lg">{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
            <p>{t("about.p3")}</p>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.94, x: 24 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease }}
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-[32px] bg-gradient-to-br from-emerald-200/50 to-sky-200/40 blur-2xl opacity-70" />
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
                <img
                  src="/team-bionet.jpg"
                  alt="Les techniciens BIONET SOLUTIONS en intervention"
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"; }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 3. VALEURS ──────────────────────────────────────────────── */}
      <section className="section bg-slate-50">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-12">
              {t("about.values_title")}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {["v1", "v2", "v3", "v4"].map((k) => {
              const Icon = ICONS[k];
              return (
                <StaggerItem key={k}>
                  <motion.div
                    className="rounded-3xl bg-white border border-slate-200 p-7 ring-soft h-full"
                    whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                    data-testid={`value-${k}`}
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
                      <Icon className="h-5 w-5 text-brand-green" />
                    </div>
                    <div className="mt-5 font-heading font-bold text-slate-900">
                      {t(`about.values.${k}.t`)}
                    </div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {t(`about.values.${k}.d`)}
                    </p>
                  </motion.div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 4. CTA FINAL ────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-[#166534] via-emerald-700 to-sky-800">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-sky-300/20 blur-3xl" />
        </div>
        <div className="container-x relative text-center">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-white">
              {t("about.cta_title")}
            </h2>
            <p className="mt-4 text-emerald-100 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              {t("about.cta_subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#166534] font-semibold px-7 py-3.5 text-sm hover:bg-slate-50 transition-colors group"
                data-testid="about-cta-quote"
              >
                {t("common.cta_quote")}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+33649502801"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-semibold px-7 py-3.5 text-sm hover:bg-white/10 transition-colors"
                data-testid="about-cta-phone"
              >
                <Phone className="h-4 w-4" /> 06 49 50 28 01
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
